import { pdfFooterHeightPx } from '../utils/pdfLetterhead';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Card, Chip, Divider, Stack, Typography, Button, Alert, Grid, Sheet, AspectRatio, Link, Modal, ModalDialog, IconButton, DialogTitle, DialogContent, DialogActions, RadioGroup, Radio, Textarea, FormControl, FormLabel, Avatar } from '@mui/joy';
import { IconWrapper } from '../../../components/IconWrapper';
import { FaCheck, FaTimes, FaFacebookF, FaWhatsapp, FaInstagram, FaEnvelope, FaPhoneAlt, FaDownload } from 'react-icons/fa';
import ImageCanvasViewer from '../../../components/ImageCanvasViewer';
import { WindowGeneratorBaySchematicSvg, WindowGeneratorThumbnail, WindowGeneratorSvg } from '../../../components/windowGeneratorRender';
import { formatProfileDefinition, getDesignerSummaryRows, isBayLikePayload, mergeModalStateIntoPayload } from '../components/QuoteVisualDesignerCard';
import { directSupabase } from '../../../lib/supabaseClient';
import { parseSemanticTricklePosition } from '../utils/trickleVentPosition';
import PublicDocPdfPreview from '../components/PublicDocPdfPreview';
import { FinalSurveyReviewTable } from '../components/FinalSurveyReviewTable';
import { reviewSectionsFromSnapshot } from '../utils/surveyDeltaReview';
import { renderDescriptionHtml } from '../utils/richTextDescription';
import { documentDisplayRef } from '../utils/invoiceLedger';
import { isPaidInvoiceDocument } from '../../../shared/pdf/documentPdfStyles';
import { stripTrailingEmptyPdfPagesHtml, withPdfPrintHeightReset } from '../../../shared/pdf/prepareExportHtml';
import { sanitizePublicInvoicePdfHtml } from '../utils/sanitizePublicInvoicePdfHtml';
import { isDoorOnlyTypeId } from '../utils/doorTypes';
import {
  defaultPaymentTermsInDays,
  invoiceDueDate,
  paymentTermsDaysDisplay,
  toDateOnly,
} from '../utils/invoicePaymentTerms';

function publicCommercialNoun(kind?: string | null): { lower: string; title: string } {
  const normalized = String(kind || '').trim().toLowerCase();
  if (normalized === 'estimate') return { lower: 'estimate', title: 'Estimate' };
  if (normalized === 'invoice') return { lower: 'invoice', title: 'Invoice' };
  return { lower: 'quotation', title: 'Quotation' };
}

const publicDocPdfPreviewCache = new Map<string, string>();

function hashForCacheKey(input: string): string {
  const s = String(input || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${s.length}:${(h >>> 0).toString(16)}`;
}

type PublicDocResponse = {
  shareLinkId: string;
  sentVersionId?: string | null;
  sentVersionNumber?: number | null;
  isOutdatedQuote?: boolean;
  sentAt?: string | null;
  validUntil?: string | null;
  emailActivityId: string | null;
  openLogged: boolean;
  openedAt: string | null;
  viewerKind?: 'customer' | 'internal';
  debug?: {
    docId?: string;
    docKind?: string;
    docTypeUsed?: string;
    organisationId?: string;
    templateFound?: boolean;
    pdfConfigFound?: boolean;
    baseHref?: string;
  };
  organisationName: string | null;
  /** Brand code, mailbox, website and logo from Admin → Organisations. */
  organisationBrand?: { code?: string | null; senderEmail?: string | null; websiteUrl: string | null; logoPath: string | null };
  messages?: Array<{
    id: string;
    senderKind: 'customer' | 'staff';
    senderName: string | null;
    senderUserId?: string | null;
    message: string;
    sourceAction?: string | null;
    edited?: boolean;
    createdAt: string | null;
    updatedAt?: string | null;
  }>;
  customer?: {
    title: string | null;
    forename: string | null;
    surname: string | null;
    address: string | null;
    email: string | null;
    phone: string | null;
  };
  document: {
    kind: string;
    status?: string | null;
    acceptedAt?: string | null;
    lifecycleRef: string;
    invoiceRef?: string | null;
    title: string;
    description: string | null;
    currency: string;
    vatEnabled: boolean | null;
    totals: {
      netTotal: number | null;
      vatAmount: number | null;
      grossTotal: number | null;
    };
    issuedAt: string | null;
    invoiceRole?: string | null;
    paymentTermsInDays?: number | null;
    dueDate?: string | null;
    projectDuration: { days: number | null; hours: number | null };
    deposit: { hasDeposit: boolean; depositAmount: number | null; depositPercentage: number | null };
    visualDesignerComponents: Array<{
      name: string | null;
      qty: number | null;
      meta: any;
    }>;
  };
  pdf: { url: string | null; html?: string | null };
  finalSurveyReapproval?: {
    deltaId: string;
    finalSurveyId: string | null;
    classification: string | null;
    pending: boolean;
    acceptedAt: string | null;
    comparedAgainst?: 'first_survey' | 'previous_final';
    changes: {
      notes: string[];
      headerFieldDiffs: string[];
      visualDimensionalChanged: boolean;
      commercialLineCountChanged: boolean;
      materialScopeOrPricingChanged: boolean;
      review?: Array<{
        id: string;
        title: string;
        rows: Array<{
          id: string;
          group?: string;
          item: string | null;
          label: string;
          original: string | null;
          final: string | null;
        }>;
      }>;
    } | null;
    first?: { header?: Record<string, unknown>; lines?: any[] } | null;
    final?: { header?: Record<string, unknown>; lines?: any[] } | null;
  } | null;
};

function normalizePublicDocResponse(raw: any): PublicDocResponse {
  const parseMaybeJson = (value: any) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return {};
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const normalizeVisualMeta = (value: any) => {
    const meta = parseMaybeJson(value);
    const specs = parseMaybeJson((meta as any)?.specs);
    const options = parseMaybeJson((specs as any)?.options);
    return {
      ...meta,
      specs: {
        ...specs,
        options,
      },
    };
  };

  // Back-compat: support older shape (pre-allowlist) to avoid runtime crashes
  const shareLinkId = String(raw?.shareLinkId || '');
  const base: PublicDocResponse = {
    shareLinkId,
    sentVersionId: raw?.sentVersionId != null ? String(raw.sentVersionId) : null,
    sentVersionNumber: raw?.sentVersionNumber != null ? Number(raw.sentVersionNumber) : null,
    isOutdatedQuote: !!raw?.isOutdatedQuote,
    sentAt: raw?.sentAt != null ? String(raw.sentAt) : null,
    validUntil: raw?.validUntil != null ? String(raw.validUntil) : null,
    emailActivityId: raw?.emailActivityId != null ? String(raw.emailActivityId) : null,
    openLogged: !!raw?.openLogged,
    openedAt: raw?.openedAt != null ? String(raw.openedAt) : null,
    viewerKind: raw?.viewerKind === 'internal' ? 'internal' : raw?.viewerKind === 'customer' ? 'customer' : undefined,
    debug: raw?.debug,
    organisationName: raw?.organisationName != null ? String(raw.organisationName) : null,
    organisationBrand: {
      code: raw?.organisationBrand?.code ? String(raw.organisationBrand.code) : null,
      senderEmail: raw?.organisationBrand?.senderEmail ? String(raw.organisationBrand.senderEmail) : null,
      websiteUrl: raw?.organisationBrand?.websiteUrl ? String(raw.organisationBrand.websiteUrl) : null,
      logoPath: raw?.organisationBrand?.logoPath ? String(raw.organisationBrand.logoPath) : null,
    },
    messages: Array.isArray(raw?.messages)
      ? raw.messages.map((m: any) => ({
          id: String(m?.id || ''),
          senderKind: m?.senderKind === 'staff' ? 'staff' : 'customer',
          senderName: m?.senderName != null ? String(m.senderName) : null,
          senderUserId: m?.senderUserId != null ? String(m.senderUserId) : null,
          message: String(m?.message || ''),
          sourceAction: m?.sourceAction != null ? String(m.sourceAction) : null,
          edited: !!m?.edited,
          createdAt: m?.createdAt != null ? String(m.createdAt) : null,
          updatedAt: m?.updatedAt != null ? String(m.updatedAt) : null,
        }))
      : [],
    customer: raw?.customer,
    document: raw?.document,
    pdf: raw?.pdf || { url: null, html: null },
  } as any;

  // If customer is missing but old fields exist on document, synthesize
  if (!base.customer && raw?.document) {
    const d = raw.document;
    if (d.customerTitle != null || d.customerFirstName != null || d.customerLastName != null) {
      base.customer = {
        title: d.customerTitle ?? null,
        forename: d.customerFirstName ?? null,
        surname: d.customerLastName ?? null,
        address: null,
        email: null,
        phone: null,
      };
    }
  }

  // If document is old shape with totals at top-level, map into new structure
  if (raw?.document && raw.document.totals == null && (raw.document.netTotal != null || raw.document.grossTotal != null || raw.document.vatAmount != null)) {
    const d = raw.document;
    base.document = {
      kind: String(d.kind || ''),
      lifecycleRef: String(d.lifecycleRef || ''),
      invoiceRef: d.invoiceRef ?? null,
      title: String(d.title || ''),
      description: d.description ?? null,
      currency: String(d.currency || 'GBP'),
      vatEnabled: d.vatEnabled ?? null,
      totals: {
        netTotal: d.netTotal ?? null,
        vatAmount: d.vatAmount ?? null,
        grossTotal: d.grossTotal ?? null,
      },
      issuedAt: d.issuedAt ?? null,
      projectDuration: { days: null, hours: null },
      deposit: { hasDeposit: false, depositAmount: null, depositPercentage: null },
      visualDesignerComponents: [],
    };
  }

  // Normalize visual-designer components to a stable shape (meta can be object/json-string/nested)
  const vdComponentsRaw = Array.isArray((base as any)?.document?.visualDesignerComponents)
    ? (base as any).document.visualDesignerComponents
    : [];
  (base as any).document.visualDesignerComponents = vdComponentsRaw.map((c: any) => {
    const metaSource = c?.meta ?? c?.metaJson ?? c?.meta_json ?? {};
    return {
      name: c?.name ?? null,
      qty: c?.qty ?? null,
      meta: normalizeVisualMeta(metaSource),
    };
  });

  // Minimal validation
  if (!base.shareLinkId || !base.document || !base.document.title) {
    throw new Error('Public viewer response is missing required fields. Please redeploy the public-doc edge function and refresh.');
  }

  const rawReapproval = raw?.finalSurveyReapproval;
  if (rawReapproval && typeof rawReapproval === 'object') {
    const changes = rawReapproval.changes && typeof rawReapproval.changes === 'object'
      ? rawReapproval.changes
      : null;
    base.finalSurveyReapproval = {
      deltaId: String(rawReapproval.deltaId || ''),
      finalSurveyId: rawReapproval.finalSurveyId != null ? String(rawReapproval.finalSurveyId) : null,
      classification: rawReapproval.classification != null ? String(rawReapproval.classification) : null,
      pending: !!rawReapproval.pending,
      acceptedAt: rawReapproval.acceptedAt != null ? String(rawReapproval.acceptedAt) : null,
      comparedAgainst: rawReapproval.comparedAgainst === 'previous_final' ? 'previous_final' : 'first_survey',
      changes: changes
        ? {
            notes: Array.isArray(changes.notes) ? changes.notes.map((n: any) => String(n)) : [],
            headerFieldDiffs: Array.isArray(changes.headerFieldDiffs)
              ? changes.headerFieldDiffs.map((n: any) => String(n))
              : [],
            visualDimensionalChanged: !!changes.visualDimensionalChanged,
            commercialLineCountChanged: !!changes.commercialLineCountChanged,
            materialScopeOrPricingChanged: !!changes.materialScopeOrPricingChanged,
            review: Array.isArray((changes as any).review)
              ? (changes as any).review.map((section: any) => ({
                  id: String(section?.id || ''),
                  title: String(section?.title || ''),
                  rows: Array.isArray(section?.rows)
                    ? section.rows.map((row: any) => ({
                        id: String(row?.id || ''),
                        group: row?.group != null ? String(row.group) : undefined,
                        item: row?.item != null && String(row.item).trim() ? String(row.item) : null,
                        label: String(row?.label || ''),
                        original: row?.original != null && String(row.original).trim() ? String(row.original) : null,
                        final: row?.final != null && String(row.final).trim() ? String(row.final) : null,
                      }))
                    : [],
                })).filter((section: { title: string; rows: unknown[] }) => section.rows.length > 0)
              : undefined,
          }
        : null,
      first: rawReapproval.first && typeof rawReapproval.first === 'object'
        ? {
            header: (rawReapproval.first as any).header && typeof (rawReapproval.first as any).header === 'object'
              ? (rawReapproval.first as any).header
              : {},
            lines: Array.isArray((rawReapproval.first as any).lines) ? (rawReapproval.first as any).lines : [],
          }
        : null,
      final: rawReapproval.final && typeof rawReapproval.final === 'object'
        ? {
            header: (rawReapproval.final as any).header && typeof (rawReapproval.final as any).header === 'object'
              ? (rawReapproval.final as any).header
              : {},
            lines: Array.isArray((rawReapproval.final as any).lines) ? (rawReapproval.final as any).lines : [],
          }
        : null,
    };
  } else {
    base.finalSurveyReapproval = null;
  }

  return base;
}

function toAbsoluteAssetUrl(pathOrUrl?: string | null): string {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  try {
    return new URL(raw, window.location.origin).toString();
  } catch {
    return raw;
  }
}

function buildPdfHeaderFooterOptions(opts: { headerImagePath?: string | null; footerImagePath?: string | null; footerHeightPx?: number }) {
  const headerSrc = toAbsoluteAssetUrl(opts.headerImagePath || '');
  const footerSrc = toAbsoluteAssetUrl(opts.footerImagePath || '');
  const sideMarginPx = 36;
  const headerHeightPx = 130;
  const footerHeightPx = opts.footerHeightPx ?? 66;
  const headerGapPx = 24;
  const footerGapPx = 14;
  const topMarginPx = headerSrc ? (headerHeightPx + headerGapPx) : sideMarginPx;
  const bottomMarginPx = footerSrc ? (footerHeightPx + footerGapPx) : sideMarginPx;
  const headerTemplate = headerSrc
    ? `<div style="width:100%;height:${headerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${headerSrc}" alt="PDF Header" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
    : '';
  const footerTemplate = footerSrc
    ? `<div style="width:100%;height:${footerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${footerSrc}" alt="PDF Footer" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
    : '';
  return {
    displayHeaderFooter: !!(headerTemplate || footerTemplate),
    headerTemplate,
    footerTemplate,
    margin: {
      top: `${topMarginPx}px`,
      right: `${sideMarginPx}px`,
      bottom: `${bottomMarginPx}px`,
      left: `${sideMarginPx}px`,
    },
  };
}

function extractPdfAssetsFromHtml(html: string, invoiceRole?: string | null): {
  cleanedHtml: string;
  headerImagePath: string;
  footerImagePath: string;
} {
  const srcRe = /src="([^"]+)"/i;
  const headerBlockRe = /<div[^>]*class="[^"]*header-image[^"]*"[^>]*>[\s\S]*?<\/div>/i;
  const footerBlockRe = /<footer[^>]*id="doc-footer"[^>]*>[\s\S]*?<\/footer>/i;
  const headerMatch = html.match(headerBlockRe);
  const footerMatch = html.match(footerBlockRe);
  const headerSrc = headerMatch?.[0]?.match(srcRe)?.[1] || '';
  const footerSrc = footerMatch?.[0]?.match(srcRe)?.[1] || '';
  const cleanedHtml = withPdfPrintHeightReset(
    stripTrailingEmptyPdfPagesHtml(
      sanitizePublicInvoicePdfHtml(
        html
          .replace(headerBlockRe, '')
          .replace(footerBlockRe, '')
          // Keep only page size here; margins are controlled by pdfOptions.
          .replace(/@page\s*\{[^}]*\}/gi, '@page { size: A4; }'),
        invoiceRole,
      ),
    ),
  );
  return {
    cleanedHtml,
    headerImagePath: headerSrc,
    footerImagePath: footerSrc,
  };
}

const money = (currency: string, v: number | null | undefined) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(Number(v));
  } catch {
    return String(v);
  }
};

const capitalizeFirstLetter = (value: string): string => {
  const text = String(value || '');
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const toPublicImageUrl = (path?: string | null): string => {
  if (!path) return '';
  const p = String(path);
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (p.startsWith('/')) return p;
  return `/${p}`;
};

const STAFF_AVATAR_SRC = '/images/brand/keystone-favicon.png';
const CUSTOMER_AVATAR_SRC = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" rx="32" fill="%23eef2f7"/><circle cx="32" cy="24" r="12" fill="%23a0aec0"/><path d="M12 54c2-11 10-17 20-17s18 6 20 17" fill="%23a0aec0"/></svg>';

/** Fit live WG SVG thumbs inside fixed-height preview tiles without cropping. */
const WG_PREVIEW_THUMB_BOX_SX = {
  cursor: 'zoom-in',
  borderRadius: 'sm',
  overflow: 'hidden',
  bgcolor: '#d9dee7',
  height: 150,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto !important',
    height: 'auto !important',
    display: 'block',
  },
  '&:hover': { boxShadow: 'sm' },
} as const;

function getDesignerSpecsRowsPublic(meta: any): Array<{ label: string; value: string }> {
  return getDesignerSummaryRows(meta as any).filter((r) => !!String(r?.value || '').trim());
}

const NAVY = '#212D3B';
const GOLD = '#FCC14D';

const ordinal = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
};

const formatDateLong = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  return `${ordinal(day)} ${month} ${year}`;
};

const formatDateTime24 = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

export const PublicDocViewerPage: React.FC<{
  /** Saved-document mock of the customer page. Does not fetch, transmit, or persist. */
  offlinePreview?: Record<string, any> | null;
}> = ({ offlinePreview = null }) => {
  const { token: paramToken } = useParams();
  const location = useLocation();
  // Support both /public/doc/:token route and root-level /:token paths
  const token = offlinePreview
    ? ''
    : (paramToken || location.pathname.split('/').filter(Boolean)[0] || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState<'accept' | 'reject' | 'query' | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewPdfState, setPreviewPdfState] = useState<'idle' | 'rendering' | 'ready' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [invalidReason, setInvalidReason] = useState<'nullified' | 'expired' | 'invalid' | null>(null);
  const [offlineActionNote, setOfflineActionNote] = useState<string | null>(null);
  const [data, setData] = useState<PublicDocResponse | null>(() => (
    offlinePreview ? normalizePublicDocResponse(offlinePreview) : null
  ));
  const [threadMessage, setThreadMessage] = useState('');
  const [threadBusy, setThreadBusy] = useState<'create' | 'update' | 'delete' | null>(null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [lightbox, setLightbox] = useState<{ src: string; title: string } | null>(null);
  const [wgLightbox, setWgLightbox] = useState<null | {
    payload: any;
    view: 'external' | 'internal';
    mode: 'design' | 'presentation';
    schematic?: boolean;
    title: string;
  }>(null);
  const [actionModal, setActionModal] = useState<
    | null
    | {
        kind: 'accept' | 'rejectOrQuery';
        step: 'form' | 'submitted';
        choice: 'query' | 'reject';
        queryText: string;
        rejectReason: string;
        error?: string;
        submittedResponse?: any;
      }
  >(null);
  const PUBLIC_URL = String(process.env.PUBLIC_URL || '').trim();
  const fallbackLogoSrc = PUBLIC_URL ? `${PUBLIC_URL}/images/brand/keystone-logo.svg` : '/images/brand/keystone-logo.svg';
  // Preferred doc-viewer logo (user-supplied). If it doesn't exist, we fall back to the normal brand logo.
  const preferredLogoSrc = PUBLIC_URL
    ? `${PUBLIC_URL}/pdf-export-assets/keystone-glazing-document-logo.png`
    : '/pdf-export-assets/keystone-glazing-document-logo.png';
  const [headerLogoSrc, setHeaderLogoSrc] = useState<string>(preferredLogoSrc);
  // Use the brand's own logo when Admin → Organisations sets one
  const brandLogoPath = data?.organisationBrand?.logoPath || null;
  // The Glazing logo, website and social links are only shown for Keystone Glazing documents
  const brandCode = data?.organisationBrand?.code || null;
  const isGlazingBrand = brandCode
    ? brandCode === 'KSG'
    : !data?.organisationName || /glazing/i.test(data.organisationName);
  useEffect(() => {
    if (!brandLogoPath) {
      if (data && !isGlazingBrand) setHeaderLogoSrc(fallbackLogoSrc);
      return;
    }
    setHeaderLogoSrc(/^https?:/i.test(brandLogoPath) ? brandLogoPath : `${PUBLIC_URL}${brandLogoPath.startsWith('/') ? '' : '/'}${brandLogoPath}`);
  }, [brandLogoPath, PUBLIC_URL, data, isGlazingBrand, fallbackLogoSrc]);

  const edgeUrls = useMemo(() => {
    const base =
      String(process.env.REACT_APP_SUPABASE_URL || '').trim() ||
      'https://fggiylzoktmnjfslrpyw.supabase.co';
    const t = String(token || '').trim();
    const root = `${base.replace(/\/+$/g, '')}/functions/v1/public-doc`;
    return {
      get: `${root}?token=${encodeURIComponent(t)}`,
      accept: `${root}/accept?token=${encodeURIComponent(t)}`,
      respond: `${root}/respond?token=${encodeURIComponent(t)}`,
      messages: `${root}/messages?token=${encodeURIComponent(t)}`,
    };
  }, [token]);

  const buildEdgeHeaders = useCallback(async (includeJson: boolean) => {
    const anonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
    const headers: Record<string, string> = {};
    if (anonKey) {
      headers['apikey'] = anonKey;
      headers['Authorization'] = `Bearer ${anonKey}`;
    }

    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }

    // If a staff user is logged into Jarvis in this browser, include their JWT so the edge function
    // can treat this as an internal preview (won't mark customer opened).
    try {
      const { data: { session } } = await directSupabase.auth.getSession();
      const staffJwt = session?.access_token || localStorage.getItem('sb-access-token') || '';
      if (staffJwt && staffJwt !== anonKey) {
        headers['x-jarvis-user-jwt'] = staffJwt;
      }
    } catch {
      // ignore
    }

    return headers;
  }, []);

  // Ensure Poppins is available for this page (no impact on iframe preview).
  useEffect(() => {
    const id = 'poppins-font-link';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (offlinePreview) {
      setLoading(false);
      setError(null);
      setInvalidReason(null);
      setData(normalizePublicDocResponse(offlinePreview));
      return;
    }
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setInvalidReason(null);
        setData(null);

        const t = String(token || '').trim();
        if (!t) throw new Error('Missing token');

        const headers = await buildEdgeHeaders(false);
        const res = await fetch(edgeUrls.get, { method: 'GET', headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const err = new Error(String(body?.error || `Request failed (${res.status})`));
          (err as any).reason = String(body?.reason || '').trim().toLowerCase();
          (err as any).status = Number(res.status || 0);
          throw err;
        }
        if (!alive) return;
        setData(normalizePublicDocResponse(body));
      } catch (e: any) {
        if (!alive) return;
        const reason = String(e?.reason || '').trim().toLowerCase();
        const status = Number(e?.status || 0);
        if (reason === 'nullified') {
          setInvalidReason('nullified');
        } else if (reason === 'expired') {
          setInvalidReason('expired');
        } else if (status === 404) {
          setInvalidReason('invalid');
        } else {
          setInvalidReason(null);
        }
        setError(e?.message || 'Failed to load document');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [buildEdgeHeaders, edgeUrls.get, offlinePreview, token]);

  useEffect(() => {
    let cancelled = false;
    let nextUrl: string | null = null;
    const html = String(data?.pdf?.html || '').trim();
    const cacheKey = html ? `${hashForCacheKey(html)}:inv-sanitize-3` : '';
    if (!html) {
      setPreviewPdfState('idle');
      setPreviewPdfUrl((prev) => {
        if (prev && !Array.from(publicDocPdfPreviewCache.values()).includes(prev)) URL.revokeObjectURL(prev);
        return null;
      });
      return () => {
        cancelled = true;
        if (nextUrl && !Array.from(publicDocPdfPreviewCache.values()).includes(nextUrl)) URL.revokeObjectURL(nextUrl);
      };
    }
    const cached = publicDocPdfPreviewCache.get(cacheKey);
    if (cached) {
      setPreviewPdfUrl(cached);
      setPreviewPdfState('ready');
      return () => {
        cancelled = true;
      };
    }
    setPreviewPdfState('rendering');
    (async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 25000);
      try {
        const extracted = extractPdfAssetsFromHtml(html, data?.document?.invoiceRole);
        const pdfOptions = buildPdfHeaderFooterOptions({
          headerImagePath: extracted.headerImagePath,
          footerImagePath: extracted.footerImagePath,
          footerHeightPx: await pdfFooterHeightPx(extracted.footerImagePath),
        });
        const res = await fetch('/api/export-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({ html: extracted.cleanedHtml, pdfOptions }),
        });
        if (!res.ok) {
          if (!cancelled) setPreviewPdfState('failed');
          return;
        }
        if (cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        nextUrl = URL.createObjectURL(blob);
        publicDocPdfPreviewCache.set(cacheKey, nextUrl);
        setPreviewPdfUrl((prev) => {
          if (prev && prev !== nextUrl && !Array.from(publicDocPdfPreviewCache.values()).includes(prev)) URL.revokeObjectURL(prev);
          return nextUrl;
        });
        setPreviewPdfState('ready');
      } catch {
        if (!cancelled) setPreviewPdfState('failed');
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();
    return () => {
      cancelled = true;
      if (nextUrl && !Array.from(publicDocPdfPreviewCache.values()).includes(nextUrl)) URL.revokeObjectURL(nextUrl);
    };
  }, [data?.pdf?.html]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Typography level="h3">Loading…</Typography>
      </Box>
    );
  }

  if (error) {
    const blockedTitle =
      invalidReason === 'nullified'
        ? null
        : invalidReason === 'expired'
          ? 'This document link has expired'
          : invalidReason === 'invalid'
            ? 'This document link is invalid'
            : null;
    const blockedMessage =
      invalidReason === 'nullified'
        ? 'This document is no longer available, please contact us for more information.'
        : error;
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Alert color={blockedTitle ? 'warning' : 'danger'}>
          <Stack spacing={0.5}>
            {blockedTitle && <Typography level="title-sm">{blockedTitle}</Typography>}
            <Typography level="body-sm">{blockedMessage}</Typography>
          </Stack>
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Typography>No document found.</Typography>
      </Box>
    );
  }

  const doc = data.document;
  const commercialNoun = publicCommercialNoun(doc.kind);
  const c = data.customer ?? { title: null, forename: null, surname: null, address: null, email: null, phone: null };
  const descriptionHtml = renderDescriptionHtml(doc.description);
  const customerName = `${c.title ? c.title + ' ' : ''}${c.forename || ''} ${c.surname || ''}`.trim();
  const durationText =
    (doc.projectDuration?.days || doc.projectDuration?.hours)
      ? `${doc.projectDuration?.days != null ? `${doc.projectDuration.days} days` : ''}${doc.projectDuration?.days != null && doc.projectDuration?.hours != null ? ' · ' : ''}${doc.projectDuration?.hours != null ? `${doc.projectDuration.hours} hours` : ''}`
      : '—';
  const depositText =
    doc.deposit?.hasDeposit
      ? `${doc.deposit.depositAmount != null ? money(doc.currency, doc.deposit.depositAmount) : '—'}${doc.deposit.depositPercentage != null ? ` (${Math.round(Number(doc.deposit.depositPercentage) * 100)}%)` : ''}`
      : 'No';
  const isVatEnabled = doc.vatEnabled === true;
  const isVatDisabled = doc.vatEnabled === false;
  const siteHref = data.organisationBrand?.websiteUrl
    || (isGlazingBrand ? 'https://keystoneglazing.co.uk' : '');
  const brandEmail = data.organisationBrand?.senderEmail || 'info@keystoneglazing.co.uk';

  const validUntilDisplay = (() => {
    if (data.validUntil) return data.validUntil;
    // Back-compat: if validUntil isn't present yet, compute from sentAt
    const baseIso = data.sentAt ?? doc.issuedAt ?? null;
    if (!baseIso) return null;
    const baseDate = new Date(baseIso);
    if (Number.isNaN(baseDate.getTime())) return null;
    return new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
  })();
  const isDocumentExpired = (() => {
    if (offlinePreview) return false;
    if (!validUntilDisplay) return false;
    const ms = Date.parse(validUntilDisplay);
    return !Number.isNaN(ms) && ms < Date.now();
  })();

  const handleExportPdf = async () => {
    try {
      if (!data?.pdf?.url && !data?.pdf?.html) return;
      setExportingPdf(true);

      const fileBase = `${commercialNoun.title}-${String(documentDisplayRef(doc) || token || 'Document')
        .replace(/[\\/:*?"<>|]+/g, '-')
        .trim() || 'Document'}`;
      const fileName = `${fileBase}.pdf`;

      let blob: Blob | null = null;
      if (data.pdf.html) {
        const extracted = extractPdfAssetsFromHtml(data.pdf.html, data.document.invoiceRole);
        const pdfOptions = buildPdfHeaderFooterOptions({
          headerImagePath: extracted.headerImagePath,
          footerImagePath: extracted.footerImagePath,
          footerHeightPx: await pdfFooterHeightPx(extracted.footerImagePath),
        });
        const res = await fetch('/api/export-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: extracted.cleanedHtml, pdfOptions }),
        });
        if (!res.ok) {
          const msg = await res.text().catch(() => '');
          throw new Error(msg || `PDF export failed (${res.status})`);
        }
        blob = await res.blob();
      } else if (data.pdf.url) {
        const res = await fetch(data.pdf.url);
        if (!res.ok) throw new Error(`Failed to download PDF (${res.status})`);
        blob = await res.blob();
      }

      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export PDF:', e);
      // Fallback: open preview URL in a new tab if it already exists.
      if (data?.pdf?.url) {
        window.open(data.pdf.url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const openLightbox = (src: string, title: string) => {
    const s = String(src || '').trim();
    if (!s) return;
    setLightbox({ src: s, title: String(title || 'Image') });
  };

  const blockOfflineWrite = () => {
    if (!offlinePreview) return false;
    setOfflineActionNote('Accept, reject, and messages stay on this screen. Nothing is sent or saved.');
    return true;
  };

  const onAccept = async () => {
    if (blockOfflineWrite()) return;
    try {
      setSubmitting('accept');
      const headers = await buildEdgeHeaders(true);
      const res = await fetch(edgeUrls.accept, { method: 'POST', headers, body: JSON.stringify({}) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // If already accepted, treat as a final state and prevent repeat submissions.
        if (res.status === 409 && (
          String(body?.documentStatus || '').trim() === 'accepted'
          || body?.finalSurveyReapprovalAccepted
        )) {
          setData((prev) => {
            if (!prev) return prev;
            const next: PublicDocResponse = {
              ...prev,
              document: { ...prev.document, status: 'accepted' },
            };
            if (body?.finalSurveyReapprovalAccepted && prev.finalSurveyReapproval) {
              next.finalSurveyReapproval = {
                ...prev.finalSurveyReapproval,
                pending: false,
                acceptedAt: prev.finalSurveyReapproval.acceptedAt || new Date().toISOString(),
              };
            }
            return next;
          });
          throw new Error(
            body?.finalSurveyReapprovalAccepted
              ? 'This Final Survey has already been accepted.'
              : `This ${commercialNoun.lower} has already been accepted.`,
          );
        }
        throw new Error(String(body?.error || `Request failed (${res.status})`));
      }

      setData((prev) => {
        if (!prev) return prev;
        const next: PublicDocResponse = {
          ...prev,
          document: { ...prev.document, status: 'accepted' },
        };
        if (body?.finalSurveyReapprovalAccepted && prev.finalSurveyReapproval) {
          next.finalSurveyReapproval = {
            ...prev.finalSurveyReapproval,
            pending: false,
            acceptedAt: new Date().toISOString(),
          };
        }
        return next;
      });

      setActionModal({
        kind: 'accept',
        step: 'form',
        choice: 'query',
        queryText: '',
        rejectReason: '',
        submittedResponse: body,
      });
    } catch (e: any) {
      setActionModal({
        kind: 'accept',
        step: 'form',
        choice: 'query',
        queryText: '',
        rejectReason: '',
        error: e?.message || 'Failed to accept',
      });
    } finally {
      setSubmitting(null);
    }
  };

  const onRejectOrQuery = () => {
    setActionModal({
      kind: 'rejectOrQuery',
      step: 'form',
      choice: 'query',
      queryText: '',
      rejectReason: '',
    });
  };

  const submitAction = async () => {
    if (blockOfflineWrite()) return;
    if (!actionModal) return;
    if (actionModal.kind !== 'rejectOrQuery') return;

    const choice = actionModal.choice;
    if (choice === 'query') {
      const msg = actionModal.queryText.trim();
      if (!msg) {
        alert('Please enter your query.');
        return;
      }
    }
    if (choice === 'reject') {
      if (!actionModal.rejectReason.trim()) {
        alert('Please choose a reason.');
        return;
      }
    }

    try {
      setSubmitting(choice);

      const payload =
        choice === 'query'
          ? { choice: 'query', message: actionModal.queryText.trim() }
          : { choice: 'reject', reason: actionModal.rejectReason.trim() };

      const headers = await buildEdgeHeaders(true);
      const res = await fetch(edgeUrls.respond, { method: 'POST', headers, body: JSON.stringify(payload) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // If already rejected, treat as final state and prevent repeat submissions.
        if (res.status === 409 && String(body?.documentStatus || '').trim() === 'rejected') {
          setData((prev) => (prev ? { ...prev, document: { ...prev.document, status: 'rejected' } } : prev));
          throw new Error(`This ${commercialNoun.lower} has already been rejected.`);
        }
        throw new Error(String(body?.error || `Request failed (${res.status})`));
      }

      if (choice === 'reject') {
        setData((prev) => (prev ? { ...prev, document: { ...prev.document, status: 'rejected' } } : prev));
      }
      if (Array.isArray(body?.messages)) {
        setData((prev) => (prev ? normalizePublicDocResponse({ ...prev, messages: body.messages }) : prev));
      }

      setActionModal(prev => (prev ? { ...prev, step: 'submitted', submittedResponse: body } : prev));
    } catch (e: any) {
      const msg = e?.message || 'Failed to submit';
      setActionModal(prev => (prev ? { ...prev, step: 'submitted', submittedResponse: { ok: false, error: msg } } : prev));
    } finally {
      setSubmitting(null);
    }
  };

  const submitThreadMessage = async () => {
    if (blockOfflineWrite()) return;
    const message = threadMessage.trim();
    if (!message) {
      setThreadError('Please enter a message.');
      return;
    }
    try {
      setThreadBusy('create');
      setThreadError(null);
      const headers = await buildEdgeHeaders(true);
      const res = await fetch(edgeUrls.messages, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'create', message }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(String(body?.error || `Request failed (${res.status})`));
      if (Array.isArray(body?.messages)) {
        setData((prev) => (prev ? normalizePublicDocResponse({ ...prev, messages: body.messages }) : prev));
      }
      setThreadMessage('');
      setEditingMessageId(null);
      setEditingMessageText('');
    } catch (e: any) {
      setThreadError(e?.message || 'Failed to send message');
    } finally {
      setThreadBusy(null);
    }
  };

  const saveStaffMessageEdit = async () => {
    if (blockOfflineWrite()) return;
    const messageId = String(editingMessageId || '').trim();
    const message = editingMessageText.trim();
    if (!messageId || !message) return;
    try {
      setThreadBusy('update');
      setThreadError(null);
      const headers = await buildEdgeHeaders(true);
      const res = await fetch(edgeUrls.messages, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update', messageId, message }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(String(body?.error || `Request failed (${res.status})`));
      if (Array.isArray(body?.messages)) {
        setData((prev) => (prev ? normalizePublicDocResponse({ ...prev, messages: body.messages }) : prev));
      }
      setEditingMessageId(null);
      setEditingMessageText('');
    } catch (e: any) {
      setThreadError(e?.message || 'Failed to update message');
    } finally {
      setThreadBusy(null);
    }
  };

  const deleteStaffMessage = async (messageId: string) => {
    if (blockOfflineWrite()) return;
    const id = String(messageId || '').trim();
    if (!id) return;
    try {
      setThreadBusy('delete');
      setThreadError(null);
      const headers = await buildEdgeHeaders(true);
      const res = await fetch(edgeUrls.messages, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'delete', messageId: id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(String(body?.error || `Request failed (${res.status})`));
      if (Array.isArray(body?.messages)) {
        setData((prev) => (prev ? normalizePublicDocResponse({ ...prev, messages: body.messages }) : prev));
      }
      if (editingMessageId === id) {
        setEditingMessageId(null);
        setEditingMessageText('');
      }
    } catch (e: any) {
      setThreadError(e?.message || 'Failed to delete message');
    } finally {
      setThreadBusy(null);
    }
  };

  const previewLabel = previewPdfState === 'rendering'
    ? 'Preparing PDF…'
    : (previewPdfUrl || data.pdf?.url ? 'PDF Ready' : (data.pdf?.html ? 'HTML Preview' : '—'));
  const docStatus = String((data.document as any)?.status || '').trim().toLowerCase();
  const isInvoice = String(doc.kind || '').trim() === 'Invoice';
  const invoicePaymentCards = (() => {
    if (!isInvoice) return null;
    const termsDays = typeof doc.paymentTermsInDays === 'number' && Number.isFinite(doc.paymentTermsInDays)
      ? Math.floor(doc.paymentTermsInDays)
      : defaultPaymentTermsInDays(doc.invoiceRole);
    const issuedDateOnly = toDateOnly(doc.issuedAt);
    const dueDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(doc.dueDate || '').trim())
      ? String(doc.dueDate).trim()
      : (issuedDateOnly ? invoiceDueDate(issuedDateOnly, termsDays) : null);
    return {
      terms: paymentTermsDaysDisplay(termsDays),
      dueDate: dueDateOnly ? formatDateLong(`${dueDateOnly}T12:00:00`) : '—',
    };
  })();
  const displayDocTitle = !isInvoice ? capitalizeFirstLetter(doc.title || 'Document') : (doc.title || 'Document');
  const finalReapproval = data.finalSurveyReapproval || null;
  const finalReviewSections = finalReapproval
    ? (
      (finalReapproval.changes?.review && finalReapproval.changes.review.length > 0)
        ? finalReapproval.changes.review
        : reviewSectionsFromSnapshot({
          first: finalReapproval.first || undefined,
          final: finalReapproval.final || undefined,
        })
    )
    : [];
  const isFinalReapprovalPending = !!(finalReapproval && finalReapproval.pending);
  const isFinalReapprovalAccepted = !!(finalReapproval && !finalReapproval.pending);
  const alreadyAccepted = (
    docStatus === 'accepted'
    || docStatus === 'invoiced'
    || docStatus === 'paid'
    || docStatus === 'cash'
    || !!String((data.document as any)?.acceptedAt || '').trim()
  );
  // Final reapproval links may accept while the quote is already Accepted.
  // First / outdated links must never re-open Accept after a later transmission.
  const acceptDisabled = isFinalReapprovalPending
    ? (isDocumentExpired || isFinalReapprovalAccepted)
    : (
      alreadyAccepted
      || docStatus === 'rejected'
      || isDocumentExpired
      || !!data.isOutdatedQuote
      || !!finalReapproval
    );
  const canManageThread = data.viewerKind === 'internal';
  const threadMessages = (Array.isArray(data.messages) ? [...data.messages] : []).sort((a, b) => {
    const aMs = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bMs = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bMs - aMs;
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        bgcolor: '#f5f6f8',
        fontFamily: 'Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        '& *': { fontFamily: 'inherit', boxSizing: 'border-box' },
        '& img, & svg, & video, & iframe, & table': { maxWidth: '100%' },
      }}
    >
      <Modal open={!!actionModal} onClose={() => setActionModal(null)}>
        <ModalDialog layout="center" variant="outlined" sx={{ width: { xs: '94vw', sm: 520 }, maxWidth: 520, borderRadius: 'md' }}>
          {!actionModal ? null : actionModal.kind === 'accept' ? (
              <>
                <DialogTitle sx={{ color: NAVY, fontWeight: 900 }}>Thank you</DialogTitle>
                <DialogContent sx={{ color: 'rgba(0,0,0,0.78)' }}>
                  {actionModal.error ? (
                    <Alert color="danger">{actionModal.error}</Alert>
                  ) : (actionModal as any)?.submittedResponse?.finalSurveyReapprovalAccepted ? (
                    <>Thank you for accepting the Final Survey changes. Your updated specification is now confirmed.</>
                  ) : (
                    <>Thank you for accepting this {doc.kind.toLowerCase()}. You will receive a deposit request email shortly.</>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="solid"
                    sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 900, '&:hover': { bgcolor: '#f8b93c' } }}
                    onClick={() => setActionModal(null)}
                  >
                    OK
                  </Button>
                </DialogActions>
              </>
            ) : actionModal.step === 'submitted' ? (
              <>
                <DialogTitle sx={{ color: NAVY, fontWeight: 900 }}>Thanks — we’ve received that</DialogTitle>
                <DialogContent sx={{ color: 'rgba(0,0,0,0.78)' }}>
                  {actionModal.submittedResponse && (actionModal.submittedResponse as any).ok === false ? (
                    <Alert color="danger">
                      {String((actionModal.submittedResponse as any).error || 'Failed to submit')}
                    </Alert>
                  ) : (
                    <>
                      {actionModal.choice === 'query'
                        ? 'Your query has been received. We’ll get back to you shortly.'
                        : 'Your rejection has been recorded. Thank you for the feedback.'}
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button variant="outlined" onClick={() => setActionModal(null)}>
                    Close
                  </Button>
                </DialogActions>
              </>
            ) : (
              <>
                <DialogTitle sx={{ color: NAVY, fontWeight: 900 }}>Reject or send a query</DialogTitle>
                <DialogContent sx={{ color: 'rgba(0,0,0,0.78)' }}>
                  <FormControl sx={{ mt: 0.5 }}>
                    <FormLabel>What would you like to do?</FormLabel>
                    <RadioGroup
                      value={actionModal.choice}
                      onChange={(e) => setActionModal(prev => (prev ? { ...prev, choice: String((e.target as HTMLInputElement).value) as any } : prev))}
                    >
                      <Radio value="query" label="Query (ask a question)" />
                      <Radio value="reject" label={`Reject ${commercialNoun.lower}`} />
                    </RadioGroup>
                  </FormControl>

                  {actionModal.choice === 'query' ? (
                    <FormControl sx={{ mt: 1 }}>
                      <FormLabel>Your message</FormLabel>
                      <Textarea
                        minRows={4}
                        value={actionModal.queryText}
                        onChange={(e) => setActionModal(prev => (prev ? { ...prev, queryText: e.target.value } : prev))}
                        placeholder="Type your question here…"
                      />
                    </FormControl>
                  ) : (
                    <FormControl sx={{ mt: 1 }}>
                      <FormLabel>Reason</FormLabel>
                      <RadioGroup
                        value={actionModal.rejectReason}
                        onChange={(e) => setActionModal(prev => (prev ? { ...prev, rejectReason: String((e.target as HTMLInputElement).value) } : prev))}
                      >
                        <Radio value="I am not ready to undertake this project" label="I am not ready to undertake this project" />
                        <Radio value={`The ${commercialNoun.lower} is too expensive`} label={`The ${commercialNoun.lower} is too expensive`} />
                        <Radio value="I have doubts about the suitabaility of the proposed solution" label="I have doubts about the suitabaility of the proposed solution" />
                      </RadioGroup>
                    </FormControl>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button variant="outlined" onClick={() => setActionModal(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 900, '&:hover': { bgcolor: '#f8b93c' } }}
                    loading={submitting === actionModal.choice}
                    onClick={submitAction}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={!!lightbox} onClose={() => setLightbox(null)}>
        <ModalDialog
          layout="center"
          variant="outlined"
          sx={{
            p: 1.5,
            width: { xs: '94vw', sm: '86vw', md: '70vw' },
            maxWidth: 980,
            borderRadius: 'md',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography level="title-md" sx={{ fontWeight: 900, color: NAVY, pr: 1, wordBreak: 'break-word' }}>
              {lightbox?.title || 'Image'}
            </Typography>
            <IconButton variant="outlined" onClick={() => setLightbox(null)} aria-label="Close image preview">
              <IconWrapper icon={FaTimes} />
            </IconButton>
          </Stack>

          <Sheet
            variant="soft"
            sx={{
              borderRadius: 'md',
              overflow: 'hidden',
              bgcolor: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Box
              component="img"
              src={lightbox?.src || ''}
              alt={lightbox?.title || 'Image'}
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: { xs: '70vh', sm: '74vh' },
                objectFit: 'contain',
              }}
            />
          </Sheet>
        </ModalDialog>
      </Modal>

      <Modal open={!!wgLightbox} onClose={() => setWgLightbox(null)}>
        <ModalDialog
          layout="center"
          variant="outlined"
          sx={{
            p: 1.5,
            width: wgLightbox?.schematic
              ? { xs: '96vw', sm: '90vw', md: '86vw' }
              : { xs: '92vw', sm: '68vw', md: '64vw' },
            maxWidth: wgLightbox?.schematic ? 1200 : 860,
            maxHeight: wgLightbox?.schematic ? '94vh' : '90vh',
            borderRadius: 'md',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
            <Typography level="title-md" sx={{ fontWeight: 900, color: NAVY, pr: 1, wordBreak: 'break-word' }}>
              {wgLightbox?.title || 'Window preview'}
            </Typography>
            <IconButton variant="outlined" onClick={() => setWgLightbox(null)} aria-label="Close window preview">
              <IconWrapper icon={FaTimes} />
            </IconButton>
          </Stack>
          <Sheet
            variant="soft"
            sx={{
              borderRadius: 'md',
              overflow: 'visible',
              bgcolor: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              p: { xs: 0.75, sm: 1 },
              flex: '0 1 auto',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '& svg': {
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: wgLightbox?.schematic
                    ? { xs: 'calc(94vh - 88px)', sm: 'calc(94vh - 80px)' }
                    : { xs: 'calc(90vh - 88px)', sm: 'calc(90vh - 80px)' },
                  display: 'block',
                },
              }}
            >
              {wgLightbox?.payload ? (
                wgLightbox.schematic ? (
                  <WindowGeneratorBaySchematicSvg payload={wgLightbox.payload} widthPx={960} />
                ) : (
                  <WindowGeneratorSvg
                    payload={wgLightbox.payload}
                    widthPx={700}
                    view={wgLightbox.view}
                    mode={wgLightbox.mode}
                  />
                )
              ) : null}
            </Box>
          </Sheet>
        </ModalDialog>
      </Modal>

      {/* HERO HEADER */}
      <Sheet sx={{ bgcolor: NAVY, color: '#fff', px: { xs: 1.25, sm: 2, md: 3 }, py: { xs: 2, sm: 2.5, md: 3 }, boxShadow: 'sm', overflow: 'hidden' }}>
        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Stack spacing={0.75} sx={{ flex: { sm: '1 1 auto' }, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  flexWrap: 'wrap',
                  rowGap: 0.5,
                  width: '100%',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                {[
                  { href: 'tel:01243302841', label: 'Call 01243 302841', icon: FaPhoneAlt },
                  { href: `mailto:${brandEmail}`, label: 'Email', icon: FaEnvelope },
                  ...(isGlazingBrand
                    ? [
                      { href: 'https://www.facebook.com/keystoneglazing/', label: 'Facebook', icon: FaFacebookF },
                      { href: 'https://www.instagram.com/keystoneglazing/?hl=en', label: 'Instagram', icon: FaInstagram },
                    ]
                    : []),
                  { href: 'https://api.whatsapp.com/send/?phone=447386326666', label: 'WhatsApp', icon: FaWhatsapp },
                ].map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.href}
                    target={s.href.startsWith('http') ? '_blank' : undefined}
                    rel={s.href.startsWith('http') ? 'noreferrer' : undefined}
                    size="sm"
                    variant="plain"
                    aria-label={s.label}
                    sx={{
                      color: GOLD,
                      bgcolor: 'transparent',
                      width: { xs: 34, sm: 40 },
                      height: { xs: 34, sm: 40 },
                      fontSize: { xs: 14, sm: 16 },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255)',
                      },
                    }}
                  >
                    <IconWrapper icon={s.icon} />
                  </IconButton>
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                pb: { xs: 1.5, sm: 0 },
              }}
            >
            <Box
              component="img"
              src={headerLogoSrc}
              alt={data.organisationName || 'Keystone'}
              loading="eager"
              onError={() => {
                // Avoid infinite loop if fallback also fails.
                if (headerLogoSrc !== fallbackLogoSrc) setHeaderLogoSrc(fallbackLogoSrc);
              }}
              sx={{
                width: { xs: 'min(100%, 200px)', sm: 300 },
                height: { xs: 'auto', sm: 60 },
                maxHeight: { xs: 48, sm: 60 },
                flexShrink: 0,
                objectFit: 'contain',
                display: 'block',
                bgcolor: 'transparent',
                mx: { xs: 'auto', sm: 0 },
              }}
            />
            </Box>

            <Stack spacing={0.25} sx={{ minWidth: 0, flex: { sm: '1 1 auto' }, width: { xs: '100%', sm: 'auto' } }}>
              <Typography level="title-md" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins',fontWeight: 900, lineHeight: 1.1 }}>
                {`Your ${commercialNoun.title} Is Ready`}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins' }}>
                {isInvoice ? 'Review your invoice and keep for your records.' : 'Review your document and proceed when ready.'}
              </Typography>
            </Stack>
              </Stack>
            </Stack>

          <Stack spacing={0.35} sx={{ textAlign: { xs: 'left', sm: 'right' }, width: { xs: '100%', sm: 'auto' }, minWidth: 0, flexShrink: 0 }}>
            <Chip
              variant="solid"
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'flex-end' },
                bgcolor: GOLD,
                color: '#fff',
                border: '1px solid rgba(0,0,0,0.08)',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              {doc.kind || 'Document'}
            </Chip>

            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Ref: <b style={{ color: '#fff' }}>{documentDisplayRef(doc)}</b>
            </Typography>

            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Issued: <b style={{ color: '#fff' }}>{formatDateLong(doc.issuedAt ?? data.sentAt)}</b>
            </Typography>

          </Stack>
        </Box>

        {/* Gold accent divider */}
        <Box sx={{ maxWidth: 980, mx: 'auto', mt: 2, height: 3, borderRadius: 999, bgcolor: GOLD, opacity: 0.95 }} />
      </Sheet>

      {/* BODY */}
      <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, py: { xs: 2, sm: 2.5, md: 3 }, width: '100%', minWidth: 0 }}>
        <Stack sx={{ maxWidth: 980, mx: 'auto', width: '100%', minWidth: 0 }} spacing={{ xs: 1.75, sm: 2.25 }}>
          {offlinePreview && (
            <Alert color="neutral">
              Offline preview of the saved document. This is not a transmission and nothing is saved.
              {offlineActionNote ? ` ${offlineActionNote}` : ''}
            </Alert>
          )}
          {!offlinePreview && data.viewerKind === 'internal' && (
            <Alert color="neutral">
              Staff preview mode: this view is recorded separately and does not mark the customer as “opened”.
            </Alert>
          )}
          {data.isOutdatedQuote && (
            <Alert color="warning" variant="soft">
              OUTDATED QUOTE: a newer version has been sent for this reference.
            </Alert>
          )}

          {/* Status strip */}
          <Card
            variant="outlined"
            sx={isPaidInvoiceDocument(doc.kind, doc.status) ? {
              borderColor: 'rgba(16, 185, 129, 0.45)',
              bgcolor: 'rgba(16, 185, 129, 0.08)',
            } : {
              borderColor: 'rgba(252, 193, 77, 0.55)',
              bgcolor: 'rgba(252, 193, 77, 0.10)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.5}
              sx={{ p: { xs: 1.25, sm: 2 } }}
            >
              <Stack spacing={0.6}>
                {isPaidInvoiceDocument(doc.kind, doc.status) ? (
                  <>
                    <Chip
                      size="lg"
                      variant="soft"
                      color="success"
                      sx={{ alignSelf: 'flex-start', fontWeight: 900 }}
                    >
                      Paid
                    </Chip>
                    <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                      This invoice has been marked paid. The document preview below includes a PAID stamp.
                    </Typography>
                  </>
                ) : alreadyAccepted || String(doc.status || '').trim() === 'accepted' ? (
                  <>
                    <Chip
                      size="lg"
                      variant="soft"
                      color="success"
                      sx={{ alignSelf: 'flex-start', fontWeight: 900 }}
                    >
                      {isFinalReapprovalAccepted
                        ? 'Final Survey accepted'
                        : isFinalReapprovalPending
                          ? `${commercialNoun.title} accepted — review Final changes`
                          : 'Accepted'}
                    </Chip>
                    <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                      {isFinalReapprovalPending
                        ? `Your original ${commercialNoun.lower} is accepted. Please review the Final Survey changes below and confirm.`
                        : 'Thank you — we’ve received your approval and our team has been notified.'}
                    </Typography>
                  </>
                ) : data.isOutdatedQuote ? (
                  <>
                    <Chip
                      size="lg"
                      variant="soft"
                      color="warning"
                      sx={{ alignSelf: 'flex-start', fontWeight: 900 }}
                    >
                      {`Outdated ${commercialNoun.lower}`}
                    </Chip>
                    <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                      A newer transmission has been sent for this reference. Accept / reject is only available on the latest link.
                    </Typography>
                  </>
                ) : String(doc.status || '').trim() === 'rejected' ? (
                  <>
                    <Chip
                      size="lg"
                      variant="soft"
                      color="danger"
                      sx={{ alignSelf: 'flex-start', fontWeight: 900 }}
                    >
                      Rejected
                    </Chip>
                    <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                      Thank you — we’ve received your response. If you’d like any changes, please get in touch and we can update the document.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                      {`${commercialNoun.title} ready for review`}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                      {isInvoice
                        ? 'This secure page is unique to your invoice. Please review the summary and document preview below.'
                        : `This secure page is unique to your ${commercialNoun.lower}. Please review the summary and document preview below.`}
                    </Typography>
                  </>
                )}
              </Stack>

              <Stack spacing={0.25} sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 0, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                {data.organisationName && (
                  <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                    Company: <b style={{ color: NAVY }}>{data.organisationName}</b>
                  </Typography>
                )}
                <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                  {isInvoice ? 'Invoice Date' : 'Opened'}:{' '}
                  <b style={{ color: NAVY }}>
                    {isInvoice
                      ? (doc.issuedAt ? formatDateLong(doc.issuedAt) : '—')
                      : (data.openedAt ? formatDateLong(data.openedAt) : '—')}
                  </b>
                </Typography>
                {data.sentVersionNumber != null && (
                  <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                    Version: <b style={{ color: NAVY }}>v{data.sentVersionNumber}</b>
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Card>

          {/* Main content grid */}
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              minWidth: 0,
              gridTemplateColumns: {
                xs: '1fr',
                lg: (doc.visualDesignerComponents || []).length > 0 ? '1.15fr 0.85fr' : '1fr',
              },
              gap: { xs: 1.75, sm: 2.25 },
              alignItems: 'start',
            }}
          >
            {/* LEFT COLUMN */}
            <Stack spacing={{ xs: 1.75, sm: 2.25 }} sx={{ minWidth: 0, width: '100%' }}>
              {/* Title + meta */}
              <Card variant="outlined">
                <Stack spacing={1.25} sx={{ p: 2 }}>
                  <Typography level="h3" sx={{ color: NAVY, fontWeight: 1000, wordBreak: 'break-word' }}>
                    {displayDocTitle}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(33,45,59,0.10)', color: NAVY, fontWeight: 800 }}>
                      {doc.kind}
                    </Chip>
                    <Chip size="sm" variant="outlined" sx={{ borderColor: 'rgba(33,45,59,0.35)', color: NAVY, fontWeight: 800 }}>
                      {documentDisplayRef(doc)}
                    </Chip>
                  </Stack>

                  {customerName && (
                    <>
                      <Divider />
                      <Stack spacing={0.25}>
                        <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          Prepared for
                        </Typography>
                        <Typography level="title-md" sx={{ fontWeight: 900 }}>
                          {customerName}
                        </Typography>
                      </Stack>

                      {(c.address || c.email || c.phone) && (
                        <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                          {c.address && <Typography level="body-sm">{c.address}</Typography>}
                          {(c.email || c.phone) && (
                            <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                              {c.email || '—'}{c.email && c.phone ? ' · ' : ''}{c.phone || ''}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              </Card>

              {/* Notes / description */}
              {doc.description && (
                <Card variant="outlined">
                  <Stack sx={{ p: 2 }} spacing={0.75}>
                    <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                      Scope of works
                    </Typography>
                    <Box
                      sx={{
                        color: 'rgba(0,0,0,0.80)',
                        fontSize: '0.875rem',
                        lineHeight: 1.7,
                        '& p': { my: 0, mb: 1.25 },
                        '& p:last-of-type': { mb: 0 },
                        '& ul, & ol': { my: 0, pl: 3 },
                      }}
                      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                  </Stack>
                </Card>
              )}

              {/* Visual designer components */}
              {(doc.visualDesignerComponents || []).length > 0 && (
                <Card variant="outlined" sx={{ p: 0 }}>
                  <Stack sx={{ p: 1.5 }} spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ minWidth: 0 }}>
                      <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                        Your Fenestration Items
                      </Typography>
                      <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(252,193,77,0.14)', border: '1px solid rgba(252,193,77,0.35)', color: NAVY, fontWeight: 900 }}>
                        {doc.visualDesignerComponents.length} item{doc.visualDesignerComponents.length === 1 ? '' : 's'}
                      </Chip>
                    </Stack>

                    <Grid container spacing={{ xs: 0.75, sm: 1.25 }} sx={{ mt: 0, width: '100%', minWidth: 0, maxWidth: '100%' }}>
                      {doc.visualDesignerComponents.map((comp, idx) => {
                        const meta = comp?.meta || {};
                        const style = meta?.window_style || {};
                        const specs = meta?.specs || {};
                        const isV2Lite = String(meta?.designer_version || '').trim().toLowerCase() === 'v2_lite';
                        const imgExternal = toPublicImageUrl(style?.preview_path_external || style?.preview_path || style?.previewPath || '');
                        const imgInternal = toPublicImageUrl(style?.preview_path_internal || '');
                        const imgPresExternal = toPublicImageUrl(style?.preview_path_presentation_external || '');
                        const imgPresInternal = toPublicImageUrl(style?.preview_path_presentation_internal || '');
                        const imgDesignExternal = toPublicImageUrl(style?.preview_path_design_external || '');
                        const imgDesignInternal = toPublicImageUrl(style?.preview_path_design_internal || '');
                        const isUploadedStyleMeta = String(style?.source || '').trim().toLowerCase() === 'upload';
                        const uploadedImageSlotsForCard = [
                          { label: 'Presentation · Outside', url: imgPresExternal || toPublicImageUrl(style?.preview_path_external || '') },
                          { label: 'Presentation · Inside', url: imgPresInternal || toPublicImageUrl(style?.preview_path_internal || '') },
                          { label: 'Design · Outside', url: imgDesignExternal },
                          { label: 'Design · Inside', url: imgDesignInternal },
                        ].filter((slot) => !!String(slot.url || '').trim());
                        const templatePayload = style?.templatePayload;
                        let v2LitePayloadForCard: any = null;
                        if (isV2Lite && templatePayload && templatePayload.root && templatePayload.w != null && templatePayload.h != null) {
                          try {
                            const opts: any = specs?.options || {};
                            const toStr = (v: any) => (v == null ? '' : String(v));
                            const frameColorCode = toStr((specs as any)?.frame_color_code || '');
                            const frameColorExtCode = toStr((specs as any)?.frame_color_code_external || '');
                            const frameColorIntCode = toStr((specs as any)?.frame_color_code_internal || '');
                            const bayTangentWidthMm = Number(opts.bay_tangent_width_mm ?? 0) || 0;
                            v2LitePayloadForCard = (mergeModalStateIntoPayload as any)(templatePayload, {
                              frameColorCode,
                              frameColorExtCode,
                              frameColorIntCode,
                              frameColorExtName: toStr((specs as any)?.frame_color_external || ''),
                              frameColorIntName: toStr((specs as any)?.frame_color_internal || ''),
                              frameColorExtHex: toStr(opts.frame_color_hex_external || ''),
                              frameColorIntHex: toStr(opts.frame_color_hex_internal || ''),
                              preferExplicitFrameValues: true,
                              designDimsOn: opts.design_dims_on === true,
                              glassTypeCode: String((specs as any)?.glass_type_code || '').toLowerCase() === 'obscured' ? 'obscured' : 'clear',
                              widthMm: toStr((bayTangentWidthMm > 0 ? bayTangentWidthMm : specs.width_mm) ?? ''),
                              heightMm: toStr(specs.height_mm ?? ''),
                              handleColorCode: toStr(opts.handle_color_code || ''),
                              handleStyleCode: toStr(
                                opts.handle_style_code ||
                                  (isDoorOnlyTypeId(meta?.window_type?.id) ? 'lever' : 'inline')
                              ),
                              trickleVents: opts.trickle_vents === true ? 'yes' : 'no',
                              trickleVentsPosition: parseSemanticTricklePosition(opts.trickle_vents_position),
                              cillOption: (opts.cill_option as any) || 'none',
                              extTopMm: toStr(opts.ext_top_mm ?? ''),
                              extBottomMm: toStr(opts.ext_bottom_mm ?? ''),
                              extLeftMm: toStr(opts.ext_left_mm ?? ''),
                              extRightMm: toStr(opts.ext_right_mm ?? ''),
                              georgianOn: opts.georgian_bars === true ? 'yes' : opts.georgian_bars === false ? 'no' : undefined,
                              georgianCols: toStr(opts.georgian_cols ?? ''),
                              georgianRows: toStr(opts.georgian_rows ?? ''),
                              georgianWidthMm: toStr(opts.georgian_width_mm ?? ''),
                              georgianOffXPct: toStr(opts.georgian_offset_x_pct ?? ''),
                              georgianOffYPct: toStr(opts.georgian_offset_y_pct ?? ''),
                              glassStyleTilePath: toStr(opts.glass_style_tile ?? ''),
                              astragalOn: opts.astragal_bars === true ? 'yes' : opts.astragal_bars === false ? 'no' : undefined,
                              astragalCols: toStr(opts.astragal_cols ?? ''),
                              astragalRows: toStr(opts.astragal_rows ?? ''),
                              astragalWidthMm: toStr(opts.astragal_width_mm ?? ''),
                              astragalOffXPct: toStr(opts.astragal_offset_x_pct ?? ''),
                              astragalOffYPct: toStr(opts.astragal_offset_y_pct ?? ''),
                              logicalDivsBackup: (opts.logical_divs && typeof opts.logical_divs === 'object') ? opts.logical_divs : null,
                              jointStyle: (opts.joint_style as any) || 'mitre-45',
                              isDoor: isDoorOnlyTypeId(meta?.window_type?.id),
                              forceNoOpenings: String(style?.category || '').trim().toLowerCase() === 'fixed',
                            }) as any;
                          } catch {
                            v2LitePayloadForCard = null;
                          }
                        }
                        const activeTemplatePayload =
                          v2LitePayloadForCard && v2LitePayloadForCard.root && v2LitePayloadForCard.w != null && v2LitePayloadForCard.h != null
                            ? v2LitePayloadForCard
                            : templatePayload;
                        const isBayPayloadForCard = isBayLikePayload(activeTemplatePayload);
                        const title =
                          String(specs?.reference || '').trim() ||
                          String(style?.name || '').trim() ||
                          String(comp?.name || '').trim() ||
                          'Component';
                        const category = formatProfileDefinition(style?.category);
                        const specsRows = getDesignerSpecsRowsPublic(meta);

                        return (
                          <Grid key={`${title}-${idx}`} xs={12} sx={{ minWidth: 0 }}>
                            <Card variant="outlined" sx={{ height: '100%', p: 0, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                              <Stack spacing={0.9} sx={{ p: 1 }}>
                                <Stack spacing={0.5}>
                                  <Typography level="title-sm" sx={{ fontWeight: 650, color: 'rgba(0,0,0,0.82)' }}>
                                    {title}
                                  </Typography>

                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                                    {category && (
                                      <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(33,45,59,0.10)', color: NAVY, fontWeight: 800 }}>
                                        {category}
                                      </Chip>
                                    )}
                                    <Chip size="sm" variant="outlined" sx={{ borderColor: 'rgba(33,45,59,0.25)', fontWeight: 800, whiteSpace: { xs: 'normal', md: 'nowrap' } }}>
                                      Qty: {comp?.qty ?? 1}
                                    </Chip>
                                  </Box>
                                </Stack>

                                {(isV2Lite && activeTemplatePayload && activeTemplatePayload.root && activeTemplatePayload.w != null && activeTemplatePayload.h != null) ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <Grid container spacing={0.75} sx={{ p: 0.75, width: '100%', minWidth: 0, maxWidth: '100%' }}>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={WG_PREVIEW_THUMB_BOX_SX}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Presentation · Outside)`}
                                            onClick={() => setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'presentation', title: `${title} · Presentation · Outside` })}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'presentation', title: `${title} · Presentation · Outside` });
                                              }
                                            }}
                                          >
                                            <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={280} view="external" mode="presentation" />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Presentation · Outside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={WG_PREVIEW_THUMB_BOX_SX}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Presentation · Inside)`}
                                            onClick={() => setWgLightbox({ payload: activeTemplatePayload, view: 'internal', mode: 'presentation', title: `${title} · Presentation · Inside` })}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setWgLightbox({ payload: activeTemplatePayload, view: 'internal', mode: 'presentation', title: `${title} · Presentation · Inside` });
                                              }
                                            }}
                                          >
                                            <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={280} view="internal" mode="presentation" />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Presentation · Inside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={WG_PREVIEW_THUMB_BOX_SX}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Design · Outside)`}
                                            onClick={() => setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'design', title: `${title} · Design · Outside` })}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'design', title: `${title} · Design · Outside` });
                                              }
                                            }}
                                          >
                                            <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={280} view="external" mode="design" />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Design · Outside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={WG_PREVIEW_THUMB_BOX_SX}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Design · Inside)`}
                                            onClick={() => setWgLightbox({ payload: activeTemplatePayload, view: 'internal', mode: 'design', title: `${title} · Design · Inside` })}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setWgLightbox({ payload: activeTemplatePayload, view: 'internal', mode: 'design', title: `${title} · Design · Inside` });
                                              }
                                            }}
                                          >
                                            <WindowGeneratorSvg payload={activeTemplatePayload} widthPx={280} view="internal" mode="design" />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Design · Inside</Typography>
                                        </Stack>
                                      </Grid>
                                      {isBayPayloadForCard && (
                                        <Grid xs={12}>
                                          <Stack spacing={0.35}>
                                            <Box
                                              sx={WG_PREVIEW_THUMB_BOX_SX}
                                              role="button"
                                              tabIndex={0}
                                              aria-label={`Expand image for ${title} (Bay Schematic)`}
                                              onClick={() => setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'design', schematic: true, title: `${title} · Bay Schematic` })}
                                              onKeyDown={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  setWgLightbox({ payload: activeTemplatePayload, view: 'external', mode: 'design', schematic: true, title: `${title} · Bay Schematic` });
                                                }
                                              }}
                                            >
                                              <WindowGeneratorBaySchematicSvg payload={activeTemplatePayload} widthPx={280} />
                                            </Box>
                                            <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Bay Schematic</Typography>
                                          </Stack>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Sheet>
                                ) : isUploadedStyleMeta && uploadedImageSlotsForCard.length > 0 ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <Grid container spacing={1} sx={{ p: 1 }}>
                                      {uploadedImageSlotsForCard.map((slot) => (
                                        <Grid key={slot.label} xs={12} md={6} sx={{ minWidth: 0 }}>
                                          <Stack spacing={0.35}>
                                            <Box
                                              sx={{
                                                cursor: 'zoom-in',
                                                borderRadius: 'sm',
                                                overflow: 'hidden',
                                                bgcolor: '#d9dee7',
                                                '&:hover': { boxShadow: 'sm' },
                                              }}
                                              role="button"
                                              tabIndex={0}
                                              aria-label={`Expand image for ${title} (${slot.label})`}
                                              onClick={() => openLightbox(slot.url, `${title} · ${slot.label}`)}
                                              onKeyDown={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  openLightbox(slot.url, `${title} · ${slot.label}`);
                                                }
                                              }}
                                            >
                                              <ImageCanvasViewer imageUrl={slot.url} overallWidthMm={null} overallHeightMm={null} heightPx={220} marginPx={12} />
                                            </Box>
                                            <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>{slot.label}</Typography>
                                          </Stack>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Sheet>
                                ) : (imgPresExternal && imgPresInternal && imgDesignExternal && imgDesignInternal) ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <Grid container spacing={0.75} sx={{ p: 0.75, width: '100%', minWidth: 0, maxWidth: '100%' }}>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Presentation · Outside)`}
                                            onClick={() => openLightbox(imgPresExternal, `${title} · Presentation · Outside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgPresExternal, `${title} · Presentation · Outside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgPresExternal} overallWidthMm={null} overallHeightMm={null} heightPx={150} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Presentation · Outside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Presentation · Inside)`}
                                            onClick={() => openLightbox(imgPresInternal, `${title} · Presentation · Inside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgPresInternal, `${title} · Presentation · Inside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgPresInternal} overallWidthMm={null} overallHeightMm={null} heightPx={150} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Presentation · Inside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Design · Outside)`}
                                            onClick={() => openLightbox(imgDesignExternal, `${title} · Design · Outside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgDesignExternal, `${title} · Design · Outside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgDesignExternal} overallWidthMm={null} overallHeightMm={null} heightPx={150} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Design · Outside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Design · Inside)`}
                                            onClick={() => openLightbox(imgDesignInternal, `${title} · Design · Inside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgDesignInternal, `${title} · Design · Inside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgDesignInternal} overallWidthMm={null} overallHeightMm={null} heightPx={150} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Design · Inside</Typography>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Sheet>
                                ) : (imgExternal && imgInternal ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <Grid container spacing={0.75} sx={{ p: 0.75, width: '100%', minWidth: 0, maxWidth: '100%' }}>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Outside)`}
                                            onClick={() => openLightbox(imgExternal, `${title} · Outside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgExternal, `${title} · Outside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgExternal} overallWidthMm={null} overallHeightMm={null} heightPx={200} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Viewed from outside</Typography>
                                        </Stack>
                                      </Grid>
                                      <Grid xs={12} md={6} sx={{ minWidth: 0 }}>
                                        <Stack spacing={0.35}>
                                          <Box
                                            sx={{
                                              cursor: 'zoom-in',
                                              borderRadius: 'sm',
                                              overflow: 'hidden',
                                              bgcolor: '#d9dee7',
                                              '&:hover': { boxShadow: 'sm' },
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Expand image for ${title} (Inside)`}
                                            onClick={() => openLightbox(imgInternal, `${title} · Inside`)}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openLightbox(imgInternal, `${title} · Inside`);
                                              }
                                            }}
                                          >
                                            <ImageCanvasViewer imageUrl={imgInternal} overallWidthMm={null} overallHeightMm={null} heightPx={200} />
                                          </Box>
                                          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>Viewed from inside</Typography>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Sheet>
                                ) : activeTemplatePayload ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <WindowGeneratorThumbnail payload={activeTemplatePayload} heightPx={200} />
                                  </Sheet>
                                ) : imgExternal ? (
                                  <Sheet
                                    variant="soft"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.08)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                      cursor: 'zoom-in',
                                      '&:hover': { boxShadow: 'sm' },
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Expand image for ${title}`}
                                    onClick={() => openLightbox(imgExternal, title)}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openLightbox(imgExternal, title);
                                      }
                                    }}
                                  >
                                    <ImageCanvasViewer imageUrl={imgExternal} overallWidthMm={null} overallHeightMm={null} heightPx={200} />
                                  </Sheet>
                                ) : (
                                  <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    <Typography level="body-sm">No image available</Typography>
                                  </Sheet>
                                ))}

                                {specsRows.length > 0 && (
                                  <Sheet
                                    variant="plain"
                                    sx={{
                                      borderRadius: 'md',
                                      border: '1px solid rgba(0,0,0,0.10)',
                                      overflow: 'hidden',
                                      bgcolor: '#fff',
                                    }}
                                  >
                                    <Box component="table" sx={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      borderCollapse: 'collapse',
                                      tableLayout: 'fixed',
                                      '& th, & td': {
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                      },
                                      '@media (max-width: 899px)': {
                                        '& tr': {
                                          display: 'block',
                                          borderBottom: '1px solid rgba(0,0,0,0.06)',
                                        },
                                        '& tr:last-of-type': { borderBottom: 'none' },
                                        '& th, & td': {
                                          display: 'block',
                                          width: '100% !important',
                                        },
                                        '& th': {
                                          borderBottom: 'none',
                                          pb: 0.25,
                                        },
                                        '& td': {
                                          pt: 0.25,
                                          pb: 1,
                                        },
                                      },
                                    }}>
                                      <Box component="tbody">
                                        {specsRows.map((row, rowIdx) => (
                                          <Box
                                            key={`${title}-spec-${row.label}-${rowIdx}`}
                                            component="tr"
                                            sx={{
                                              borderBottom: rowIdx === specsRows.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                                            }}
                                          >
                                            <Box
                                              component="th"
                                              scope="row"
                                              sx={{
                                                width: { xs: '45%', sm: '35%' },
                                                textAlign: 'left',
                                                px: 1.25,
                                                py: 0.7,
                                                verticalAlign: 'top',
                                                color: 'rgba(0,0,0,0.62)',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.45,
                                                bgcolor: 'rgba(0,0,0,0.02)',
                                              }}
                                            >
                                              {row.label}
                                            </Box>
                                            <Box
                                              component="td"
                                              sx={{
                                                px: 1.25,
                                                py: 0.7,
                                                color: 'rgba(0,0,0,0.84)',
                                                fontSize: '0.8125rem',
                                                lineHeight: 1.45,
                                                whiteSpace: 'pre-wrap',
                                              }}
                                            >
                                              {row.value || '—'}
                                            </Box>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  </Sheet>
                                )}

                              </Stack>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Stack>
                </Card>
              )}
            </Stack>

            {/* RIGHT COLUMN */}
            <Stack spacing={{ xs: 1.75, sm: 2.25 }} sx={{ minWidth: 0, width: '100%' }}>
              {/* Details tiles */}
              <Card variant="outlined">
                <Stack sx={{ p: 2 }} spacing={1.25}>
                  <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                    Details
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {[
                      { k: 'VAT enabled', v: doc.vatEnabled == null ? '—' : (doc.vatEnabled ? 'Yes' : 'No') },
                      { k: 'Deposit', v: depositText },
                      { k: 'Project Duration', v: durationText },
                      { k: 'Document type', v: doc.kind || '—' },
                      ...(invoicePaymentCards
                        ? [
                          { k: 'Payment Terms', v: invoicePaymentCards.terms },
                          { k: 'Due Date', v: invoicePaymentCards.dueDate },
                        ]
                        : []),
                    ].map((row) => (
                      <Sheet
                        key={row.k}
                        variant="soft"
                        sx={{
                          p: 1.1,
                          borderRadius: 'md',
                          bgcolor: 'rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: 0.7 }}>
                          {row.k}
                        </Typography>
                        <Typography level="body-md" sx={{ mt: 0.25, fontWeight: 900, color: 'rgba(0,0,0,0.82)' }}>
                          {row.v}
                        </Typography>
                      </Sheet>
                    ))}
                  </Box>
                </Stack>
              </Card>

              {/* Total hero */}
              <Card
                variant="outlined"
                sx={{
                  borderColor: 'rgba(252, 193, 77, 0.75)',
                  boxShadow: 'md',
                }}
              >
                <Stack sx={{ p: 2 }} spacing={1.25}>
                  <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {isVatDisabled ? 'Project Total' : 'Project Total (incl. VAT)'}
                  </Typography>

                  <Typography
                    level="h1"
                    sx={{
                      color: NAVY,
                      fontWeight: 1000,
                      letterSpacing: -0.5,
                      lineHeight: 1.05,
                    }}
                  >
                    {money(doc.currency, doc.totals?.grossTotal)}
                  </Typography>

                  <Box sx={{ height: 3, borderRadius: 999, bgcolor: GOLD, opacity: 0.95 }} />

                  <Stack spacing={0.45}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.65)' }}>Net</Typography>
                      <Typography level="body-sm" sx={{ fontWeight: 900, color: 'rgba(0,0,0,0.80)' }}>
                        {money(doc.currency, doc.totals?.netTotal)}
                      </Typography>
                    </Stack>
                    {isVatEnabled && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.65)' }}>VAT</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 900, color: 'rgba(0,0,0,0.80)' }}>
                          {money(doc.currency, doc.totals?.vatAmount)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {isVatDisabled && (
                    <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
                      VAT is not applicable.
                    </Typography>
                  )}
                </Stack>
              </Card>

          {/* Actions (quotes/estimates only) */}
          {!isInvoice && (
            <Card variant="outlined">
              <Stack sx={{ p: 2 }} spacing={1.25}>
                <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                  {finalReapproval
                    ? (isFinalReapprovalAccepted
                      ? 'Final Survey approved'
                      : 'Review Final Survey changes')
                    : `Proceed with this ${commercialNoun.lower}`}
                </Typography>

                {finalReapproval?.changes && (
                  <Alert color="warning" variant="soft">
                    <Stack gap={1}>
                      <Typography level="title-sm" sx={{ fontWeight: 800 }}>Review Changes</Typography>
                      <Typography level="body-sm">
                        {finalReapproval.comparedAgainst === 'previous_final'
                          ? 'The following details have changed since the previous Final Survey.'
                          : `The following details have changed since the original ${commercialNoun.lower}.`}
                      </Typography>
                      <FinalSurveyReviewTable
                        sections={finalReviewSections}
                        originalHeading={finalReapproval.comparedAgainst === 'previous_final' ? 'Previous' : 'Original'}
                        emptyText="Please review the updated specification in this document."
                      />
                    </Stack>
                  </Alert>
                )}

                  {!finalReapproval && (
                  <Stack spacing={0.5}>
                    {[
                      'Locks in agreed pricing',
                      'Secures your installation slot',
                      'Generates your confirmation documentation',
                    ].map((t) => (
                      <Typography key={t} level="body-sm" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                        • {t}
                      </Typography>
                    ))}
                  </Stack>
                  )}

                  {finalReapproval && isFinalReapprovalPending && (
                    <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                      Please review the Final Survey above and the changes listed, then accept to confirm this updated specification.
                    </Typography>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      fullWidth
                      size="lg"
                      variant="solid"
                      onClick={onAccept}
                      startDecorator={<IconWrapper icon={FaCheck} />}
                      sx={{
                        bgcolor: GOLD,
                        color: NAVY,
                        fontWeight: 1000,
                        '&:hover': { bgcolor: '#f8b93c' },
                        '&:disabled': { bgcolor: 'rgba(252,193,77,0.35)', color: 'rgba(33,45,59,0.65)' },
                      }}
                      loading={submitting === 'accept'}
                      disabled={acceptDisabled || submitting != null}
                    >
                      {isFinalReapprovalAccepted
                        ? 'Final Survey accepted'
                        : isFinalReapprovalPending
                          ? 'Accept Final Survey'
                          : (alreadyAccepted ? `${commercialNoun.title} accepted` : `Accept ${commercialNoun.lower}`)}
                    </Button>

                    {!finalReapproval && !data.isOutdatedQuote && (
                    <Button
                      fullWidth
                      size="lg"
                      variant="outlined"
                      color="danger"
                      onClick={onRejectOrQuery}
                      startDecorator={<IconWrapper icon={FaTimes} />}
                      sx={{
                        borderColor: NAVY,
                        color: NAVY,
                        fontWeight: 1000,
                        '&:hover': { bgcolor: 'rgba(33,45,59,0.06)' },
                        '&:disabled': { borderColor: 'rgba(33,45,59,0.25)', color: 'rgba(33,45,59,0.45)' },
                      }}
                      loading={submitting === 'reject'}
                      disabled={acceptDisabled || submitting != null}
                    >
                      Reject / query
                    </Button>
                    )}
                  </Stack>

                  <Divider />

                  <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                    Prefer to talk first?
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                    <Link href={`mailto:${brandEmail}`} sx={{ fontWeight: 700, color: NAVY }}>
                      Email
                    </Link>{' '}
                    or{' '}
                    <Link href="tel:01243302841" sx={{ fontWeight: 700, color: NAVY }}>
                      call us
                    </Link>{' '}
                    and we’ll help.
                  </Typography>
              </Stack>
            </Card>
          )}

          {/* Document validity (quotes/estimates only) */}
          {!isInvoice && (
            <Card
              variant="outlined"
              sx={{
                borderColor: isDocumentExpired ? 'rgba(211, 47, 47, 0.45)' : 'rgba(252, 193, 77, 0.75)',
                boxShadow: 'md',
              }}
            >
              <Stack sx={{ p: 2 }} spacing={1.25}>
                <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Document validity
                </Typography>

                  <Typography
                    level="h4"
                    sx={{
                      color: isDocumentExpired ? '#9a1b1b' : NAVY,
                      fontWeight: 1000,
                      letterSpacing: -0.3,
                      lineHeight: 1.1,
                    }}
                  >
                    {validUntilDisplay
                      ? `${isDocumentExpired ? 'Expired on' : 'Valid until'} ${formatDateLong(validUntilDisplay)}`
                      : '—'}
                  </Typography>

                  <Box
                    sx={{
                      height: 3,
                      borderRadius: 999,
                      bgcolor: isDocumentExpired ? 'rgba(211,47,47,0.85)' : GOLD,
                      opacity: 0.95,
                    }}
                  />

                  <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.78)' }}>
                    {isDocumentExpired ? (
                      <>This {commercialNoun.lower} validity period has expired. Please contact us for an updated {commercialNoun.lower}.</>
                    ) : (
                      <>
                        Your {doc.kind} is valid for <b>30 days</b>
                        {validUntilDisplay ? <> until <b>{formatDateLong(validUntilDisplay)}</b>.</> : <> from the date it was sent.</>}
                      </>
                    )}
                  </Typography>
              </Stack>
            </Card>
          )}

          {/* FAQ (quotes/estimates only) */}
          {!isInvoice && (
            <Card variant="outlined" sx={{ p: 0 }}>
              <Stack sx={{ p: 2 }} spacing={1.25}>
                <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                  FAQs
                </Typography>

                <Stack spacing={1}>
                  {([
                    {
                      q: 'Are Keystone Glazing FENSA/CERTASS registered?',
                      a: (
                        <Typography level="body-sm">
                          Yes. Keystone Glazing is{' '}
                          <Link href="https://certifiedcompetent.co.uk/advice/what-is-a-competent-person-scheme/" target="_blank" rel="noreferrer">
                            CERTASS
                          </Link>{' '}
                          registered, meaning our installations comply with current Building Regulations and are independently certified.
                        </Typography>
                      ),
                    },
                    {
                      q: 'Will I receive a CERTASS certificate after installation?',
                      a: (
                        <Typography level="body-sm">
                          Yes. Once your installation is complete and registered, you will receive your official CERTASS compliance certificate for your records.
                        </Typography>
                      ),
                    },
                    {
                      q: 'Is my deposit protected by an Insurance Backed Guarantee (IBG)?',
                      a: (
                        <Typography level="body-sm">
                          Yes. Your deposit and installation are protected under the HomePro Insurance Backed Guarantee (IBG), giving you additional peace of mind.
                        </Typography>
                      ),
                    },
                    {
                      q: 'Is the deposit refundable?',
                      a: (
                        <Typography level="body-sm">
                          Deposits are generally non-refundable once materials are ordered or manufacturing has started. If you have concerns, please speak to us as early as possible.
                        </Typography>
                      ),
                    },
                    {
                      q: `Will Keystone be notified when I accept the ${commercialNoun.lower}?`,
                      a: (
                        <Typography level="body-sm">
                          {`Yes. As soon as you accept your ${commercialNoun.lower}, our team is automatically notified so we can begin preparing your project.`}
                        </Typography>
                      ),
                    },
                    {
                      q: `What happens after I accept the ${commercialNoun.lower}?`,
                      a: (
                        <Stack spacing={0.5}>
                          <Typography level="body-sm">
                          Keystone will:
                          </Typography>
                          {[
                            'Issue your deposit request',
                            'Secure materials with suppliers',
                            'Begin scheduling your installation',
                            'Keep you updated throughout the process',
                          ].map((t) => (
                            <Typography key={t} level="body-sm">
                              - {t}
                            </Typography>
                          ))}
                        </Stack>
                      ),
                    },
                  ] as Array<{ q: string; a: React.ReactNode }>).map((item) => (
                    <Sheet
                      key={item.q}
                      variant="soft"
                      sx={{
                        p: 1.25,
                        borderRadius: '10px',
                        border: '1px solid rgba(0,0,0,0.08)',
                        bgcolor: '#fff',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography level="title-sm" sx={{ fontWeight: 700, color: NAVY }}>
                          {item.q}
                        </Typography>
                        <Box sx={{ color: 'rgba(0,0,0,0.78)', lineHeight: 1.7, fontSize: 14 }}>
                          {item.a}
                        </Box>
                      </Stack>
                    </Sheet>
                  ))}
                </Stack>
              </Stack>
            </Card>
          )}
            </Stack>

            {!isInvoice && (
            <Card variant="outlined" sx={{ gridColumn: { xs: 'auto', lg: '1 / -1' } }}>
              <Stack sx={{ p: 2 }} spacing={1.25}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                    Ask us a question!
                  </Typography>
                  <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(33,45,59,0.10)', color: NAVY, fontWeight: 800 }}>
                    {threadMessages.length} message{threadMessages.length === 1 ? '' : 's'}
                  </Chip>
                </Stack>

                <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.72)' }}>
                  {canManageThread
                    ? 'Internal replies are visible here for customer follow-up.'
                    : 'Use this section if you need clarification before proceeding.'}
                </Typography>
                {isDocumentExpired && (
                  <Alert color="warning">
                    This document has expired. New responses are disabled.
                  </Alert>
                )}

                {threadError && <Alert color="danger">{threadError}</Alert>}

                <Card variant="soft" sx={{ p: 1.25, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Stack spacing={0.75}>
                    <Textarea
                      minRows={3}
                      value={threadMessage}
                      onChange={(e) => setThreadMessage(e.target.value)}
                      placeholder={canManageThread ? 'Add an internal response for this customer...' : 'Type your question here...'}
                      disabled={threadBusy != null || isDocumentExpired}
                    />
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        size="sm"
                        variant="solid"
                        loading={threadBusy === 'create'}
                        disabled={threadBusy != null || isDocumentExpired}
                        onClick={submitThreadMessage}
                        sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 900, '&:hover': { bgcolor: '#f8b93c' } }}
                      >
                        {canManageThread ? 'Post response' : 'Send message'}
                      </Button>
                    </Stack>
                  </Stack>
                </Card>

                {threadMessages.length === 0 ? (
                  <Alert color="neutral">No messages yet.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {threadMessages.map((msg) => {
                      const sourceLabel =
                        msg.sourceAction === 'reject'
                          ? 'Rejected'
                          : msg.sourceAction === 'query'
                            ? 'Query'
                            : msg.sourceAction === 'reply'
                              ? 'Reply'
                              : null;
                      const isEditing = editingMessageId === msg.id;
                      const canEdit = canManageThread && msg.senderKind === 'staff';
                      return (
                        <Card key={msg.id} variant="outlined" sx={{ bgcolor: msg.senderKind === 'staff' ? 'rgba(33,45,59,0.04)' : '#fff' }}>
                          <Stack spacing={0.75}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                <Avatar
                                  size="sm"
                                  src={msg.senderKind === 'staff' ? STAFF_AVATAR_SRC : CUSTOMER_AVATAR_SRC}
                                  alt={msg.senderKind === 'staff' ? 'Keystone Glazing' : 'Customer'}
                                  sx={{
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    bgcolor: '#fff',
                                    '& img': {
                                      objectFit: 'contain',
                                      objectPosition: 'center',
                                      padding: '2px',
                                      backgroundColor: '#fff',
                                    },
                                  }}
                                />
                                <Typography level="title-sm" sx={{ color: NAVY, fontWeight: 800 }}>
                                  {msg.senderName || (msg.senderKind === 'staff' ? 'Keystone Glazing' : 'Customer')}
                                </Typography>
                                <Chip size="sm" variant="soft" color={msg.senderKind === 'staff' ? 'primary' : 'neutral'}>
                                  {msg.senderKind === 'staff' ? 'Staff' : 'Customer'}
                                </Chip>
                                {sourceLabel && (
                                  <Chip size="sm" variant="outlined">
                                    {sourceLabel}
                                  </Chip>
                                )}
                                {msg.edited && (
                                  <Chip size="sm" variant="outlined">
                                    Edited
                                  </Chip>
                                )}
                              </Stack>
                              <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
                                {formatDateTime24(msg.createdAt)}
                              </Typography>
                            </Stack>

                            {isEditing ? (
                              <Stack spacing={0.75}>
                                <Textarea
                                  minRows={3}
                                  value={editingMessageText}
                                  onChange={(e) => setEditingMessageText(e.target.value)}
                                />
                                <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                  <Button
                                    size="sm"
                                    variant="plain"
                                    disabled={threadBusy != null || isDocumentExpired}
                                    onClick={() => {
                                      setEditingMessageId(null);
                                      setEditingMessageText('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="solid"
                                    loading={threadBusy === 'update'}
                                    disabled={threadBusy != null || isDocumentExpired}
                                    onClick={saveStaffMessageEdit}
                                  >
                                    Save
                                  </Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', color: 'rgba(0,0,0,0.82)' }}>
                                {msg.message}
                              </Typography>
                            )}

                            {canEdit && !isEditing && (
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                <Button
                                  size="sm"
                                  variant="plain"
                                  disabled={threadBusy != null || isDocumentExpired}
                                  onClick={() => {
                                    setEditingMessageId(msg.id);
                                    setEditingMessageText(msg.message);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="plain"
                                  color="danger"
                                  loading={threadBusy === 'delete'}
                                  disabled={threadBusy != null || isDocumentExpired}
                                  onClick={() => deleteStaffMessage(msg.id)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            )}
                          </Stack>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Card>
            )}

            {/* FULL-WIDTH Preview (spans both columns on desktop) */}
            <Card variant="outlined" sx={{ gridColumn: { xs: 'auto', lg: '1 / -1' } }}>
              <Stack sx={{ p: 2 }} spacing={1.25}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                    {`${commercialNoun.title} document`}
                  </Typography>

                  <Chip
                    size="sm"
                    variant="soft"
                    sx={{
                      bgcolor: (data.pdf?.url || data.pdf?.html) ? 'rgba(33,45,59,0.10)' : 'rgba(0,0,0,0.06)',
                      color: (data.pdf?.url || data.pdf?.html) ? NAVY : 'rgba(0,0,0,0.55)',
                      fontWeight: 900,
                    }}
                  >
                    {previewLabel}
                  </Chip>
                </Stack>

                {(previewPdfUrl || data.pdf?.url || data.pdf?.html) ? (
                  <PublicDocPdfPreview
                    previewPdfUrl={previewPdfUrl}
                    pdfUrl={data.pdf?.url || null}
                    pdfHtml={data.pdf?.html || null}
                    previewPdfState={previewPdfState}
                  />
                ) : (
                  <Alert color="neutral">
                    Preview is not available yet.
                  </Alert>
                )}

                {(previewPdfUrl || data.pdf?.url || data.pdf?.html) && (
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      startDecorator={<IconWrapper icon={FaDownload} />}
                      onClick={handleExportPdf}
                      loading={exportingPdf}
                      disabled={exportingPdf}
                      sx={{
                        borderColor: NAVY,
                        color: NAVY,
                        fontWeight: 1000,
                        '&:hover': { bgcolor: 'rgba(33,45,59,0.06)' },
                        '&:disabled': { borderColor: 'rgba(33,45,59,0.25)', color: 'rgba(33,45,59,0.45)' },
                      }}
                    >
                      Download PDF
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Card>
          </Box>

          {/* Footer */}
          <Stack spacing={0.6} sx={{ textAlign: 'center', pb: 2 }}>
            {isGlazingBrand && (
              <Typography level="body-sm" sx={{ color: NAVY, fontWeight: 600 }}>
                Built for Strength, Designed for Style.
              </Typography>
            )}

            <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
              © {new Date().getFullYear()} {data.organisationName || 'Keystone'}
              {siteHref && (
                <>
                  {' · '}
                  <Link
                    href={siteHref}
                    sx={{ color: NAVY, fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {siteHref.replace(/^https?:\/\//, '')}
                  </Link>
                </>
              )}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default PublicDocViewerPage;

